import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import { navLinks } from "../header/Header";

const CATEGORY_NAMES = {
  geo: "Gio Nano Plots",
  gio: "Gio Nano Plots",
  "gioo-plots": "Gio Nano Plots",
  "gio-plots": "Gio Nano Plots",
  "gioo nano plots": "Gio Nano Plots",
  "gio nano plots": "Gio Nano Plots",
  "gioo nano plot": "Gio Nano Plots",
  "gio nano plot": "Gio Nano Plots",
  market: "Land",
  residential: "Land",
  land: "Land",
  syndicate: "Syndicate Plots",
  "syndicate-plots": "Syndicate Plots",
  "gio rental yield - syndicate plots": "Syndicate Plots",
  "gio rental yield - syndicate plot": "Syndicate Plots",
  rental: "Rental Yield",
  "rental-yield": "Rental Yield",
  "rental-yield-plots": "Rental Yield",
  "gio rental yield": "Rental Yield",
  plot: "Flats / Villas",
  "flats-villas": "Flats / Villas",
  "flats/villas": "Flats / Villas",
  "plot-marketplace": "Land",
  "material-store": "Material Store",
  "professional-services": "Professional Services",
};

const getCategoryLabel = (rawType) => {
  if (!rawType) return null;
  const cleaned = rawType.trim().toLowerCase();
  if (cleaned === "all") return null;
  return CATEGORY_NAMES[cleaned] || CATEGORY_NAMES[rawType] || rawType;
};

const BreadcrumbsNav = ({ items }) => {
  const location = useLocation();

  // 1. If an explicit items array is passed as prop
  if (items && Array.isArray(items) && items.length > 0) {
    return (
      <Breadcrumbs
        aria-label="breadcrumb"
        separator="›"
        sx={{
          "& .MuiLink-root, & .MuiTypography-root": {
            fontSize: {
              xs: "0.75rem",
              sm: "0.85rem",
              md: "0.95rem",
              lg: "1rem",
              xl: "1.05rem",
            },
          },
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return isLast || !item.path ? (
            <Typography key={index} color="secondary.light" sx={{ fontWeight: 700 }}>
              {item.label}
            </Typography>
          ) : (
            <Link
              key={index}
              component={RouterLink}
              to={item.path}
              underline="hover"
              color="primary"
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  }

  // 2. Automatic dynamic breadcrumb for /search route
  if (location.pathname === "/search") {
    const searchParams = new URLSearchParams(location.search);
    const rawType = searchParams.get("property_type") || "";
    const rawQuery = (
      searchParams.get("q") ||
      searchParams.get("search") ||
      searchParams.get("region") ||
      ""
    ).trim();

    const categoryLabel = getCategoryLabel(rawType);
    const hasCategory = Boolean(categoryLabel);
    const hasQuery = Boolean(rawQuery);

    const isQuerySameAsCategory =
      hasCategory &&
      hasQuery &&
      rawQuery.toLowerCase().replace(/\s+/g, " ") ===
        categoryLabel.toLowerCase().replace(/\s+/g, " ");

    const searchCrumbs = [
      { label: "Home", path: "/" },
      ...(hasCategory || hasQuery
        ? [{ label: "Search", path: "/search" }]
        : [{ label: "Search" }]),
      ...(hasCategory
        ? hasQuery && !isQuerySameAsCategory
          ? [{ label: categoryLabel, path: `/search?property_type=${encodeURIComponent(rawType)}` }]
          : [{ label: categoryLabel }]
        : []),
      ...(hasQuery && !isQuerySameAsCategory ? [{ label: rawQuery }] : []),
    ];

    return (
      <Breadcrumbs
        aria-label="breadcrumb"
        separator="›"
        sx={{
          "& .MuiLink-root, & .MuiTypography-root": {
            fontSize: {
              xs: "0.75rem",
              sm: "0.85rem",
              md: "0.95rem",
              lg: "1rem",
              xl: "1.05rem",
            },
          },
        }}
      >
        {searchCrumbs.map((item, index) => {
          const isLast = index === searchCrumbs.length - 1;
          return isLast || !item.path ? (
            <Typography key={index} color="secondary.light" sx={{ fontWeight: 700 }}>
              {item.label}
            </Typography>
          ) : (
            <Link
              key={index}
              component={RouterLink}
              to={item.path}
              underline="hover"
              color="primary"
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  }

  // 3. Default path-based breadcrumbs for all other pages
  const pathnames = location.pathname.split("/").filter((x) => x);
  const filteredPathnames = pathnames.filter((segment) => isNaN(segment));
  const formatName = (str) =>
    str
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator="›"
      sx={{
        "& .MuiLink-root, & .MuiTypography-root": {
          fontSize: {
            xs: "0.75rem",
            sm: "0.85rem",
            md: "0.95rem",
            lg: "1rem",
            xl: "1.05rem",
          },
        },
      }}
    >
      <Link component={RouterLink} to="/" underline="hover" color="primary">
        Home
      </Link>

      {filteredPathnames.map((segment, index) => {
        const routeTo = `/${filteredPathnames.slice(0, index + 1).join("/")}`;
        const isLast = index === filteredPathnames.length - 1;
        const matchedLink = navLinks.find((link) => link.href === routeTo);
        const displayName = getCategoryLabel(segment) || (matchedLink ? matchedLink.title : formatName(segment));

        return isLast ? (
          <Typography key={routeTo} color="secondary.light" sx={{ fontWeight: 700 }}>
            {displayName}
          </Typography>
        ) : (
          <Link
            key={routeTo}
            component={RouterLink}
            to={routeTo}
            underline="hover"
            color="primary"
          >
            {displayName}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default BreadcrumbsNav;
